import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

interface DeleteModalProps {
  open: boolean;
  onCancel: () => void;
  onDelete: () => Promise<any>;
  title?: string;
  description?: string;
  /** Warning shown on the next line, e.g. pending schedules that will also be canceled. */
  warning?: string | null;
  confirmText?: string;
  cancelText?: string;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  open,
  onCancel,
  onDelete,
  title = "Delete Chat Thread?",
  description = "Are you sure you want to delete this chat thread?",
  warning = null,
  confirmText = "Delete",
  cancelText = "Cancel",
}) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete();
    } finally {
      setLoading(false);
      onCancel(); // close modal after delete
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onCancel()}>
      <DialogTitle fontFamily="var(--theo-font-sans)" fontWeight="bold">{title}</DialogTitle>
      <DialogContent>
        <Typography fontFamily="var(--theo-font-sans)">{description}</Typography>
        {warning ? (
          <Alert
            severity="warning"
            sx={{ mt: 1.5, fontFamily: "var(--theo-font-sans)", "& .MuiAlert-message": { fontFamily: "var(--theo-font-sans)" } }}
          >
            {warning}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button sx={{ fontFamily: "var(--theo-font-sans)" }} onClick={onCancel} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          sx={{ fontFamily: "var(--theo-font-sans)" }}
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? 'Deleting...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteModal; 
